import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './assets/css/globalStyles.css';
import { MsalProvider } from '@azure/msal-react';
import { ActionIcon, Button, createTheme, Divider, Input, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import ReactDOM from 'react-dom/client';
import { pdfjs } from 'react-pdf';
import { createBrowserRouter, redirect, RouterProvider } from 'react-router';
import { ProtectedRoute } from './components/ProtectedRoute';
import { msalInstance } from './utils/MsalInstance';

// Pages
import MainApp from './apps/MainApp';
import DashboardPage from './pages/DashboardPage';
import Http404Page from './pages/Http404Page';
import LoginPage from './pages/LoginPage';
import AddUserPage from './pages/users/AddUserPage';
import SendEmailPage from './pages/it-service/SendEmailPage';
import APIKeysPage from './pages/settings/APIKeysPage';
import ChangeManagementPage from './pages/it-service/Change-Management/ChangeManagementPage';
import ChangeViewPage from './pages/it-service/Change-Management/ChangeViewPage';
import RoadmapPage from './pages/developer/RoadmapPage';
import AuditLogsPage from './pages/reporting/AuditLogsPage';
import IntegrationsPage from './pages/settings/IntegrationsPage';
import ActiveEmployeesPage from './pages/it-service/ActiveEmployeesPage';
import InactiveEmployeesPage from './pages/it-service/InactiveEmployeesPage';
import ManageEmployeePage from './pages/it-service/ManageEmployeePage';
import { ModalsProvider } from '@mantine/modals';
import SecurityRolesPage from './pages/security/SecurityRolesPage';
import AutomationsPage from './pages/settings/AutomationsPage';

// Place pages here for routing
const router = createBrowserRouter([
    {
        path: '/login',
        Component: LoginPage
    },
    {
        path: '/login?redirect=:path',
        Component: LoginPage
    },
    {
        path: '*',
        Component: Http404Page
    },
    {
        path: '/',
        element: <ProtectedRoute requiredRoles={["User"]}><MainApp /></ProtectedRoute>,
        children: [
            {
                path: '',
                Component: DashboardPage
            },
            {
                path: 'users/new',
                Component: AddUserPage
            },
            {
                path: 'it-service',
                children: [
                    {
                        index: true,
                        loader: async () => redirect("/")
                    },
                    {
                        path: 'mail',
                        element: <ProtectedRoute requiredRoles={["IT"]}><SendEmailPage /></ProtectedRoute>
                    },
                    {
                        path: 'active-employees',
                        children: [
                            {
                                index: true,
                                element: <ProtectedRoute requiredRoles={["HR", "IT"]}><ActiveEmployeesPage /></ProtectedRoute>
                            },
                            {
                                path: 'employee/:id',
                                element: <ProtectedRoute requiredRoles={["HR", "IT"]}><ManageEmployeePage /></ProtectedRoute>
                            },
                        ]
                    },
                    {
                        path: 'inactive-employees',
                        element: <ProtectedRoute requiredRoles={["HR"]}><InactiveEmployeesPage /></ProtectedRoute>
                    },
                    {
                        path: 'change-management',
                        children: [
                            {
                                index: true,
                                element: <ProtectedRoute requiredRoles={["IT"]}><ChangeManagementPage /></ProtectedRoute>
                            },
                            {
                                path: 'changes/:id',
                                element: <ProtectedRoute requiredRoles={["IT"]}><ChangeViewPage /></ProtectedRoute>
                            }
                        ]
                    }
                ]
            },
            {
                path: 'settings',
                children: [
                    {
                        index: true,
                        loader: async () => redirect("/")
                    },
                    {
                        path: 'api-keys',
                        element: <ProtectedRoute requiredRoles={["Admin"]}><APIKeysPage /></ProtectedRoute>
                    },
                    {
                        path: 'integrations',
                        element: <ProtectedRoute requiredRoles={["Admin"]}><IntegrationsPage /></ProtectedRoute>
                    },
                    {
                        path: 'automations',
                        element: <ProtectedRoute requiredRoles={["Admin", "Automation Administrator"]}><AutomationsPage /></ProtectedRoute>
                    }
                ]
            },
            {
                path: 'security',
                children: [
                    {
                        index: true,
                        loader: async () => redirect("/")
                    },
                    {
                        path: 'roles',
                        element: <ProtectedRoute requiredRoles={["Admin"]}><SecurityRolesPage /></ProtectedRoute>
                    }
                ]
            },
            {
                path: 'reporting/audit-logs',
                element: <ProtectedRoute requiredRoles={["Admin"]}><AuditLogsPage /></ProtectedRoute>
            },
            {
                path: 'developer/roadmap',
                element: <ProtectedRoute requiredRoles={["Admin"]}><RoadmapPage /></ProtectedRoute>
            }
        ]
    },
]);

// Override default theme
const theme = createTheme({
    // fontFamily: 'Ubuntu, sans-serif',
    // fontFamily: "Roboto, sans-serif",
    fontFamily: "Inter, sans-serif",
    // these breakpoints correspond to MUI's breakpoints for consistency
    breakpoints: {
        xs: "0em",
        sm: "37.5em",
        md: "56.25",
        lg: "75em",
        xl: "96em"
    },
    components: {
        Button: Button.extend({
            classNames: {
                root: 'unify-btn-normal',
            },
        }),
        ActionIcon: ActionIcon.extend({
            classNames: {
                root: 'unify-action-icon',
            }
        }),
        Divider: Divider.extend({
            classNames: {
                root: 'unify-divider'
            }
        }),
        Input: Input.extend({
            classNames: {
                input: 'unify-input',
            }
        }),
    }
});

async function bootstrap() {
    await msalInstance.initialize();

    const root = ReactDOM.createRoot(document.getElementById('root')!);
    pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.mjs";

    root.render(
        <>
            <MantineProvider theme={theme}>
                <ModalsProvider>
                    <Notifications />
                    <MsalProvider instance={msalInstance}>
                        <RouterProvider router={router} />
                    </MsalProvider>
                </ModalsProvider>
            </MantineProvider>
        </>
    );
}

bootstrap();